/**
 * Country flag + localised name renderer.
 *
 * Renders a layered control:
 *   1. Unicode regional-indicator emoji as the ALWAYS-VISIBLE baseline
 *      (🇪🇹, 🇳🇬, 🇺🇸 …). Works on macOS / iOS / Android / Linux out of
 *      the box; on Windows 11 + Chrome it also renders natively. On
 *      older Windows + some Chromium browsers without flag-emoji
 *      support, this falls back to a pair of letter glyphs (e.g.
 *      🇪🇹 may show as "ET") — still readable, never a blank cell.
 *
 *   2. A flagcdn.com <img> overlaid on top, hidden until it actually
 *      loads. If the CDN is blocked by an ad blocker / privacy
 *      extension / corporate firewall, the image never appears and
 *      the user just sees the Unicode emoji underneath — zero
 *      regression.
 *
 *   3. Localised country name via Intl.DisplayNames — "Ethiopia" /
 *      "Etiopía" / "إثيوبيا" / "エチオピア" based on browser locale.
 *
 * Why this layered approach? An ad blocker treats third-party image
 * CDNs as tracking pixels by default — every privacy-conscious
 * browser (Brave, Firefox + uBlock, Safari Strict tracking
 * prevention) will silently block flagcdn.com without telling the
 * user. The Unicode baseline guarantees the cell never goes blank.
 *
 * @author <lukman@zshift.io>
 */
import { useMemo, useState } from 'react';

const CDN = 'https://flagcdn.com';

/** ISO-3166 alpha-2 → Unicode regional-indicator emoji pair.
 *  "ET" → "🇪🇹" by mapping each letter to U+1F1E6 + (letter - 'A'). */
function isoToEmoji(code) {
  if (!code || typeof code !== 'string' || code.length !== 2) return '';
  const upper = code.toUpperCase();
  if (upper < 'AA' || upper > 'ZZ') return '';
  const A = 0x1F1E6 - 'A'.charCodeAt(0);
  return String.fromCodePoint(upper.charCodeAt(0) + A, upper.charCodeAt(1) + A);
}

/** Get the localised country name for a 2-letter ISO code. Falls
 *  back to null if the code is unrecognised or Intl.DisplayNames
 *  isn't available (very old browsers).
 *
 *  Exported so pages with country tables (Finances, Map, future
 *  geo views) can match a free-text search query against both the
 *  ISO code and the human-readable name without duplicating the
 *  Intl.DisplayNames boilerplate. */
export function countryName(code) {
  if (!code || code.length !== 2) return null;
  try {
    const locale = (typeof navigator !== 'undefined' && navigator.language) || 'en';
    const dn = new Intl.DisplayNames([locale], { type: 'region' });
    const upper = code.toUpperCase();
    const name = dn.of(upper);
    // DisplayNames returns the input unchanged for unknown codes,
    // so equality means "didn't resolve" — return null instead of
    // double-displaying the code.
    return name && name !== upper ? name : null;
  } catch (_) {
    return null;
  }
}

/**
 * Just the flag, layered. ~18px wide by default.
 * Pass `width` to scale up. `name` is the alt-text override (defaults
 * to the localised country name).
 */
export function CountryFlag({ code, width = 18 }) {
  // Three load states:
  //   'pending' — initial; <img> is fetching. Render nothing visible
  //               so the row doesn't flash an emoji-then-image swap.
  //   'ok'      — onLoad fired. Show only the image.
  //   'failed'  — onError fired (CDN blocked / 404 / offline). Show
  //               the Unicode emoji baseline as a fallback.
  // The previous layered approach rendered BOTH simultaneously,
  // which caused the emoji's letter-glyphs (on Windows ≤10) to peek
  // out behind / around the image. Strict either-or rendering keeps
  // each cell visually clean.
  const [state, setState] = useState('pending');
  const emoji = isoToEmoji(code);
  const name = countryName(code) || (code ? code.toUpperCase() : '');

  if (!code || code.length !== 2) return null;

  const iso = code.toLowerCase();
  // flagcdn widths: w20, w40, w80, w160. Pick 2× the CSS size for retina.
  const w1x = Math.max(20, Math.min(640, width * 2));
  const w2x = Math.max(20, Math.min(2560, width * 4));

  const boxStyle = {
    display: 'inline-block',
    width,
    height: Math.round(width * 0.75),  // 4:3 standard flag aspect
    verticalAlign: 'middle',
    flexShrink: 0,
    overflow: 'hidden',
    borderRadius: 2,
    // Faint shadow so flags with white at the edge (e.g. Switzerland,
    // Japan) don't merge into a white page background.
    boxShadow: '0 0 0 1px rgba(27,20,38,0.06)',
  };

  // Failed → render emoji fallback (or just the country code if the
  // OS can't render flag emoji either). One DOM node only.
  if (state === 'failed') {
    return (
      <span style={{ ...boxStyle, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(width * 0.85), fontFamily: '"Twemoji Country Flags", "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif' }} title={name} aria-label={name}>
        {emoji || code.toUpperCase()}
      </span>
    );
  }

  // Pending or OK → render only the <img>. Pending = invisible until
  // onLoad swaps state to 'ok'. No emoji rendered behind it, so
  // there's nothing to peek through.
  return (
    <img
      src={`${CDN}/w${w1x}/${iso}.png`}
      srcSet={`${CDN}/w${w1x}/${iso}.png 1x, ${CDN}/w${w2x}/${iso}.png 2x`}
      width={width}
      height={Math.round(width * 0.75)}
      alt={name}
      title={name}
      loading="lazy"
      onLoad={() => setState('ok')}
      onError={() => setState('failed')}
      style={{
        ...boxStyle,
        objectFit: 'cover',
        opacity: state === 'ok' ? 1 : 0,   // invisible during pending
        transition: 'opacity 0.15s ease',
      }}
    />
  );
}

/**
 * "🇪🇹 Ethiopia · ET" — flag + name + (optional) raw code.
 *
 * Props:
 *   code      — ISO-3166 alpha-2 (e.g. "ET"), case-insensitive
 *   showCode  — append the 2-letter code in muted mono (default false)
 *   showName  — render the country name (default true)
 *   className / style — passthrough for layout
 */
export function CountryCell({ code, showCode = false, showName = true, className, style }) {
  const name = useMemo(() => countryName(code), [code]);
  const upper = code ? code.toUpperCase() : '—';

  if (!code) {
    return <span className={className} style={style}>—</span>;
  }

  return (
    <span
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...style }}
    >
      <CountryFlag code={code} width={20} />
      {showName && name && <span>{name}</span>}
      {!showName && <span className="mono">{upper}</span>}
      {showName && showCode && (
        <span className="mono" style={{ color: 'var(--ink-3)', fontSize: 11 }}>
          {upper}
        </span>
      )}
    </span>
  );
}
